import { z } from 'zod';

/**
 * ImageVariant Zod schema
 */
export const ImageVariantSchema = z.object({
  url: z.string().url(),
  format: z.enum(['webp', 'jpeg', 'png']),
  width: z.number().positive(),
  height: z.number().positive(),
  size: z.number().nonnegative(), // file size in bytes
}).strict();

/**
 * CDN provider types
 */
export const ImageCdnProviderSchema = z.enum(['firebase', 'cloudflare', 'vercel']);

/**
 * Image placeholder data for progressive loading (blur-up effect)
 */
export const ImagePlaceholderSchema = z.object({
  /** Base64-encoded tiny placeholder image (typically 10-20px wide) */
  blurDataUrl: z.string().optional(),
  /** Dominant color hex code (fallback when blur not available) */
  dominantColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  /** BlurHash string for client-side decoding (more compact than base64) */
  blurHash: z.string().optional(),
}).strict();

/**
 * Storage metadata for CDN migration support
 */
export const ImageStorageMetadataSchema = z.object({
  /** Provider-agnostic storage path */
  storagePath: z.string(),
  /** Original filename before optimization */
  originalFilename: z.string().optional(),
  /** MIME type of original upload */
  mimeType: z.string().optional(),
  /** Upload timestamp (ISO 8601 string) */
  uploadedAt: z.string().optional(),
  /** Current CDN provider serving this image */
  cdnProvider: ImageCdnProviderSchema.optional(),
}).strict();

/**
 * OptimizedImage Zod schema - canonical validation for all packages
 *
 * Designed to be CDN-agnostic - URLs can be regenerated for any provider
 * using the storagePath in metadata
 */
export const OptimizedImageSchema = z.object({
  /** Original uploaded image (highest quality) */
  original: ImageVariantSchema,
  /** WebP version of original (if generated) */
  webp: ImageVariantSchema.optional(),
  /** Responsive variants grouped by size category */
  variants: z.object({
    small: z.array(ImageVariantSchema),   // < 600px width
    medium: z.array(ImageVariantSchema),  // 600-1200px width
    large: z.array(ImageVariantSchema),   // > 1200px width
  }).strict(),
  /** Aspect ratio for layout calculations (width / height) */
  aspectRatio: z.number().positive(),
  /** Placeholder data for progressive loading */
  placeholder: ImagePlaceholderSchema.optional(),
  /** Storage metadata for CDN migration */
  metadata: ImageStorageMetadataSchema.optional(),
}).strict();

/**
 * Image field - accepts either a plain string (a URL, or the serialized
 * OptimizedImage JSON the website builder persists via `serializeImage`) OR a
 * structured `OptimizedImage` object. Mirrors the model's `string | OptimizedImage`
 * union so a whole-doc Zod parse and the TS model AGREE — this is what lets the
 * admin create boundary drop its `websiteConfig` cast.
 */
export const imageFieldSchema = z.union([z.string(), OptimizedImageSchema]);

/**
 * Optional variant of {@link imageFieldSchema} for embedding in object schemas
 * (branding logo/icon/hero/etc., all optional on the model). Name retained for
 * backward-compatible imports.
 */
export const imageFieldValidatorBase = imageFieldSchema.optional();

/**
 * Image field validator - accepts either URL string or OptimizedImage JSON string
 * Use this for direct validation, not for embedding in schemas that use .partial()
 */
export const imageFieldValidator = z.string().refine((val) => {
  // Check if it's a valid URL
  try {
    new URL(val);
    return true;
  } catch {
    // If not a URL, check if it's valid OptimizedImage JSON
    try {
      const parsed = JSON.parse(val);
      return OptimizedImageSchema.safeParse(parsed).success;
    } catch {
      return false;
    }
  }
}, {
  message: "Must be a valid URL or OptimizedImage JSON string"
}).optional();

// Type exports using z.infer for consistency
export type ImageCdnProviderType = z.infer<typeof ImageCdnProviderSchema>;
export type ImagePlaceholderType = z.infer<typeof ImagePlaceholderSchema>;
export type ImageStorageMetadataType = z.infer<typeof ImageStorageMetadataSchema>;
export type ImageVariantType = z.infer<typeof ImageVariantSchema>;
export type OptimizedImageType = z.infer<typeof OptimizedImageSchema>;
